function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;
    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

class CartPerformance {
  static #metric_prefix = 'cart-performance';

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, `${metricName}:start`, `${metricName}:end`);
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);
    performance.measure(benchmarkName, `${metricName}:start`, `${metricName}:end`);
  }
}
// Global event delegation
document.addEventListener('click', (e) => {
  const target = e.target;
  if (target.matches('.black-overlay') || target.matches('.menu-overlay')) {
    document.querySelector('.menu-hamburger')?.classList.remove('active');
    document
      .querySelectorAll('.drawer-wpr,.search-outer,.header_wrapper > .menu-nav-wpr,.mega-menu-outer')
      ?.forEach((element) => {
        element.classList.remove('open');
      });
  }
  
});

// Global event onKeyUpEscape
function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        const relatedProductListSwiper = new Swiper('product-recommendations .product-list-slider', {
          loop: false,
          slidesPerView: 1.4,
          spaceBetween: 20,
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          },
          breakpoints: {
            576: {
              slidesPerView: 2,
              spaceBetween: 20,
            },
            768: {
              slidesPerView: 3,
              spaceBetween: 24,
            },
            992: {
              slidesPerView: 4,
              spaceBetween: 24,
            },
          },
        });
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);
// Header Menu
document.querySelector('.mob-menu-btn')?.addEventListener('click', function () {
  var targetMenu = document.querySelector('#site-menu-link');
  if (!targetMenu.classList.contains('open')) {
    targetMenu.classList.add('open');
    this.classList.add('active-btn');
  } else {
    targetMenu.classList.remove('open');
    this.classList.remove('active-btn');
  }
});
document.querySelector('.mob-menu-close-btn')?.addEventListener('click', function () {
  var targetMenu = document.querySelector('#site-menu-link');
  if (targetMenu.classList.contains('open')) {
    targetMenu.classList.remove('open');
    document.querySelector('.mob-menu-btn').classList.remove('active-btn');
  }
});
var mainNavItem = document.querySelectorAll('.nav-ul .nav-li-item');

mainNavItem.forEach((item) => {
  var navLink = item.querySelector('.nav-link');
  navLink.addEventListener('click', function (e) {
    e.target.closest('.nav-li-item').querySelector('.sub-menu')?.classList.add('active');
  });
});
var backMenuBtn = document.querySelectorAll('.sub-menu .back-menu-btn');
backMenuBtn?.forEach((item) => {
  item.addEventListener('click', function (e) {
    if (this.closest('.sub-menu')?.classList.contains('active')) {
      this.closest('.sub-menu')?.classList.remove('active');
    }
  });
});
document.querySelector('.black-overlay, .menu-overlay')?.addEventListener('click', function () {
  var targetMenu = document.querySelector('#site-menu-link');
  if (targetMenu.classList.contains('open')) {
    targetMenu.classList.remove('open');
    document.querySelector('.mob-menu-btn').classList.remove('active-btn');
    mainNavItem.forEach((item) => {
      item.querySelector('.sub-menu')?.classList.remove('active');
    });
  }
  var targetFliterDrawer = document.querySelector('#filter-drawer');
  if (targetFliterDrawer && targetFliterDrawer.classList.contains('open')) {
    targetFliterDrawer.classList.remove('open');
  }
  if(document.body.classList.contains('overflow-hidden')){
    document.body.classList.remove('overflow-hidden');
  }
});

// Set CSS variables for scrollbar width and header height
let announcementHeight = 0;
function setScrollbarWidth() {
  const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
  document.body.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
}

function setHeaderHeight() {
  const header = document.querySelector('.header .header-main-outer');
  const announcement = document.querySelector('.announcement-bar');
  if (header) {
    document.body.style.setProperty('--header-height', `${header.offsetHeight}px`);
    document.body.style.setProperty('--announcement-height', `${announcement?.offsetHeight || 0}px`);
    announcementHeight = announcement?.offsetHeight;
  }
}
// Header Sticky
function headerSticky() {
  const header = document.querySelector('.header');
  if (window.scrollY > announcementHeight) {
    document.body.style.setProperty('--sticky-announcement-height', `0px`);
  } else {
    document.body.style.setProperty('--sticky-announcement-height', `${announcementHeight - window.scrollY}px`);
  }
}
document.addEventListener('scroll', (event) => {
  headerSticky();
});
setScrollbarWidth();
setHeaderHeight();
headerSticky();
window.addEventListener('resize', () => {
  setScrollbarWidth();
  setHeaderHeight();
  headerSticky();
});

// Hero Banner Slider
const banerSwiper = new Swiper('.banner-swiper', {
  loop: true,
  slidesPerView: 1,

  // If we need pagination
  pagination: {
    el: '.swiper-pagination',
    clickable: true, 
  },
});
// Product List Slider
document.querySelectorAll('.product-list-slider').forEach((slider) => {
  new Swiper(slider, {
    loop: false,
    slidesPerView: 1.4,
    spaceBetween: 20,
    navigation: {
      nextEl: slider.parentElement.querySelector('.swiper-button-next'),
      prevEl: slider.parentElement.querySelector('.swiper-button-prev'),
    },
    breakpoints: {
      576: {
        slidesPerView: 2,
        spaceBetween: 20,
      },
      768: {
        slidesPerView: 3,
        spaceBetween: 24,
      },
      992: {
        slidesPerView: 4,
        spaceBetween: 24,
      },
    },
  });
});

// Video Play puase btn
const videoBlocks = document.querySelectorAll('.video-block');

videoBlocks.forEach((block) => {
  const videoElement = block.querySelector('video');
  const playPauseButton = block.querySelector('.video-action-btn');

  playPauseButton?.addEventListener('click', () => {
    playPauseButton.classList.toggle('playing');
    
    if (playPauseButton.classList.contains('playing')) {
      videoElement.play();
    } else {
      videoElement.pause();
    }
  });

  videoElement?.addEventListener('ended', () => {
    playPauseButton.classList.remove('playing');
  });
});
// Accordion Js
document.querySelectorAll('.accordion-items.active').forEach((item) => {
    var itemBody =  item.querySelector('.accordion-body')
    if (itemBody) {
        itemBody.style.height = `${itemBody.querySelector('.content').offsetHeight}px`;
    }
});

document.querySelectorAll('.accordion-items .accordion-button').forEach((btn) => {
  btn.addEventListener('click', function () {

    const currentItem = btn.closest('.accordion-items');
    const accordionBody = currentItem.querySelector('.accordion-body');

    const section = currentItem.parentElement;
    const itemsInSection = section.querySelectorAll('.accordion-items');

    const isOpen = currentItem.classList.contains('active');

    itemsInSection.forEach((item) => {
      if (item !== currentItem) {
        item.classList.remove('active');
        item.querySelector('.accordion-body').style.height = '0px';
      }
    });

    if (!isOpen) {
      currentItem.classList.add('active');
      accordionBody.style.height =
        accordionBody.querySelector('.content').offsetHeight + 'px';
    } else {
      currentItem.classList.remove('active');
      accordionBody.style.height = '0px';
    }

  });
});


// Announcement Bar Slider
const announcementBar = new Swiper('.announcement-outer', {
  loop: true,
  slidesPerView: 1,
});

// FAQ Tabbing Js
document.querySelectorAll('.page-faq-outer').forEach((section) => {
  const faqTabs = section.querySelectorAll('.faq-tab-list .faq-tab-item');
  const tabContents = section.querySelectorAll('.faq-tabs-outer .tabs-content-item');

  faqTabs.forEach((tab) => {
    tab.addEventListener('click', () => {

      const targetId = tab.dataset.targetTab;

      faqTabs.forEach((t) => t.classList.remove('active'));

      tab.classList.add('active');

      tabContents.forEach((content) => content.classList.remove('show'));

      const targetEl = document.getElementById(targetId);

      if (targetEl && section.contains(targetEl)) {
        targetEl.classList.add('show');
      }

      section.querySelectorAll('.accordion-items.active').forEach((item) => {
          var itemBody =  item.querySelector('.accordion-body')
          if (itemBody) {
              itemBody.style.height = `${itemBody.querySelector('.content').offsetHeight}px`;
          }
      });
    });
  });
});

document.querySelectorAll('.article-recommendations-slider').forEach((slider) => {
  new Swiper(slider, {
    loop: false,
    slidesPerView: 1,
    spaceBetween: 20,
    navigation: {
        nextEl: slider.parentElement.querySelector('.swiper-button-next'),
        prevEl: slider.parentElement.querySelector('.swiper-button-prev'),
      },
    breakpoints: {
      576: {
        slidesPerView: 2,
        spaceBetween: 20,
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 24,
      },
      992: {
        slidesPerView: 3,
        spaceBetween: 24,
      }
    },
  });
});

document.querySelectorAll('.cbd-outer-slider').forEach((slider) => {
  new Swiper(slider, {
    loop: false,
    slidesPerView: 1,
    spaceBetween: 20,
    navigation: {
      nextEl: slider.parentElement.querySelector('.swiper-button-next'),
      prevEl: slider.parentElement.querySelector('.swiper-button-prev'),
    },
    breakpoints: {
      576: {
        slidesPerView: 2,
        spaceBetween: 20,
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 24,
      },
      992: {
        slidesPerView: 3,
        spaceBetween: 24,
      }
    },
  });
});

// Contact page Form error 
document.querySelectorAll('#ContactForm a[href^="#"]').forEach(anchor => {
  anchor.addEventListener('click', function (e) {
    e.preventDefault();

    const target = document.querySelector(this.getAttribute('href'));
    const headerOffset = document.querySelector('.header .header-main-outer'); 
    const elementPosition = target.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - (headerOffset.offsetHeight + 40);

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth"
    });
  });
});

// Effet de scintillement sur les capsules ayant >18% CBD

document.addEventListener("DOMContentLoaded", () => {
  const elements = document.querySelectorAll('.pro-bottom-label, .badge.cbd_rate');
  elements.forEach(el => {

    const match = el.textContent.match(/\d+(\.\d+)?/);
    if (!match) return;

    const value = parseFloat(match[0]);

    if (value >= 18) {

      if (el.classList.contains('cbd_rate')) {
        el.classList.add('high-cbd-badge');
      } else {
        el.classList.add('high-cbd');
      }

    }

  });

});
